import React, {useContext, useEffect, useState} from 'react';
import {Box, Stack, Typography, Rating, useMediaQuery, Snackbar, Alert} from '@mui/material';
import AimdFlowQuestionBase from '../../components/aimdFlowQuestionBase';
import {useLocation, useNavigate} from 'react-router-dom';
import AimdMultiScreenBtn from '../../components/buttons/aimdMultiScreenBtn';
import {getNuralogixCallInUrl} from '../../nuralogix/faceScan';
import {useQuestionnaireApi} from '../../api/demographics';
import {useNuralogixApi} from '../../api/nuralogix';
import APIObjectsContext from '../../context/APIObjectsContext';
import {useAssessmentApi} from '../../api/assessment';
import FaceScanInstruction from '../../components/faceScanInstruction/faceScanInstruction';

const commonStylingForTypography = {
  fontSize: {xs: '14px', lg: '18px', md: '18px', sm: '16px'},
  maxWidth: '650px',
  width: '98%',
};
export const PreFaceScanPage = () => {
  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const questionnaireApi = useQuestionnaireApi();
  const nuralogixApi = useNuralogixApi();
  const assessmentApi = useAssessmentApi();
  const isSmallHeight = useMediaQuery('(max-height:700px)');
  const {userAssessmentID, setUserAssessmentID} = useContext(APIObjectsContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [isServerError, setIsServerError] = useState(false);
  const [isShowBrightnessInstructionScreen, setIsShowBrightnessInstructionScreen] = useState(false);

  //if user is not coming from demographic/edirOrScan page he should not be here
  useEffect(() => {
    const isComingFromDemographicsPage = location.state?.isComingFromDemographicsPage;
    if (!isComingFromDemographicsPage) {
      navigate('/demographics');
    }
  }, [navigate]);

  const backButtonHandler = () => {
    if (isShowBrightnessInstructionScreen) {
      setIsShowBrightnessInstructionScreen(false);
      //setting this to reflect in progress bar
      setCurrentStep(0);
    } else {
      navigate('/skyn-condition-checker', {
        state: {comingFromPreScanPage: true},
      });
    }
  };

  const onNextClickHandler = async () => {
    if (isShowBrightnessInstructionScreen) {
      await handleAssessment();
    } else {
      setIsShowBrightnessInstructionScreen(true);
      //setting this to reflect in progress bar
      setCurrentStep(1);
    }
  };

  //thsi will redirect to face scan screen
  const handleAssessment = async () => {
    try {
      //post empty assessment to create assessmetn id
      const assessmentId = await assessmentApi.postAssessment();
      setUserAssessmentID(assessmentId?.data?.id);

      getNuralogixCallInUrl(assessmentId?.data?.id, assessmentApi, questionnaireApi, nuralogixApi).then(
        ({nuralogixTokenData, configResponseData, encryptedProfile}) => {
          const nuralogixUrl = `https://scan.ai-md.com/c/${configResponseData}/${encodeURIComponent(
            encryptedProfile.toString('base64'),
          )}/${encodeURIComponent(nuralogixTokenData.Token)}/${encodeURIComponent(nuralogixTokenData.RefreshToken)}/${
            nuralogixTokenData.sessionId
          }`;
          window.localStorage.setItem('organization_id', '0');
          window.localStorage.setItem('from_doktor', 'false');
          window.localStorage.setItem('from_atp', 'false');
          window.location.href = nuralogixUrl;
        },
      );
    } catch (error) {
      setIsServerError('Failed to start face scan. Please try again.');
      console.error('Error during assessment/face scan flow:', error);
    }
  };

  return (
    <AimdFlowQuestionBase
      IsShowBackIcon={true}
      isShowMenuIcon={true}
      backButtonHandler={backButtonHandler}
      aimdLinearBarValue={(currentStep + 1) * (100 / 2)}
    >
      {!isShowBrightnessInstructionScreen ? (
        //first text instruction jsx
        <Stack alignItems="center" mb={{sm: 0, md: 0, lg: 4}} mt={{sm: 1, md: 2, lg: 9}}>
          <Typography
            variant="h4"
            sx={{
              fontSize: {xs: '20px', lg: '34px', md: '30px', sm: '30px'},
              // my: 1,
              textAlign: 'center',
              // mt:{{ sm: 0, md: 0, lg: 3 }}
            }}
          >
            Now, we will scan your face to measure your biomarkers.
          </Typography>
          <Typography
            sx={{
              marginTop: {
                lg: 3,
                xs: '14px',
                sm: 2,
                md: 2,
              },
              fontWeight: 500,

              ...commonStylingForTypography,
            }}
            textAlign="center"
          >
            Based on these results, we can curate product recommendations that will work specifically for you.
          </Typography>
          <Typography
            sx={{
              marginTop: {
                lg: 3,
                xs: '14px',
                sm: 2,
                md: 2,
              },
              fontWeight: 500,
              ...commonStylingForTypography,
            }}
            textAlign="center"
          >
            💡 Please make sure you are in a brightly lit area and the light is spread evenly on your face.
          </Typography>
          <Typography
            sx={{
              marginTop: {
                lg: 3,
                xs: '14px',
                sm: 2,
                md: 2,
              },
              fontWeight: 500,
              ...commonStylingForTypography,
            }}
            textAlign="center"
          >
            Remove your hat and eyeglasses and stay close to the camera and still during the 30-second scan to get a
            good measurement.
          </Typography>
          <Box
            sx={{
              marginTop: {
                xs: '30px',
                sm: '90px',
                md: '100px',
                lg: '90px',
              },
              maxWidth: '321px',
              width: '100%',
              height: {
                xs: '20px',
                sm: '30px',
                md: '50px',
                lg: '65px',
              },
            }}
          >
            <AimdMultiScreenBtn onClick={onNextClickHandler}></AimdMultiScreenBtn>
          </Box>
        </Stack>
      ) : (
        // brightness instruction jsx
        <Stack alignItems="center" mb={{sm: 0, md: 0, lg: 4}} mt={{sm: 1, md: 2, lg: 9}}>
          <FaceScanInstruction
            videoContainerSx={{marginY: {xs: isSmallHeight ? '5px' : '20px', lg: '30px'}}}
          ></FaceScanInstruction>
          <Box
            sx={{
              marginTop: {
                xs: isSmallHeight ? '8px' : '30px',
                sm: '90px',
                md: '100px',
                lg: '90px',
              },
              maxWidth: '321px',
              width: '100%',
              height: {
                xs: '20px',
                sm: '30px',
                md: '50px',
                lg: '65px',
              },
            }}
          >
            <AimdMultiScreenBtn onClick={onNextClickHandler}></AimdMultiScreenBtn>
          </Box>
        </Stack>
      )}
      <Snackbar
        open={isServerError}
        autoHideDuration={3000}
        onClose={() => setIsServerError(false)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert
          variant="filled"
          severity="error"
          sx={{
            display: 'flex',
            alignItems: 'center',
            textAlign: 'center',
            mb: 8,
            fontSize: {
              xs: 16,
              sm: 18,
            },
            px: {
              xs: 2,
              sm: 5,
            },
            borderRadius: 3,
          }}
        >
          {isServerError}
        </Alert>
      </Snackbar>
    </AimdFlowQuestionBase>
  );
};
